import {
  Component,
  ContentChild,
  Input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LearningMaterialData } from '@app/api/models/learning-material-data.model';
import { User } from '@app/api/user/models/user.model';
import { HttpClient } from '@angular/common/http';
import { ApiService } from '@core/services/api.service';
import {
  CreateLearningActivityRequest,
  LearningActivity,
} from '@app/api/models/learning-activity.model';
import { GlobalModalService } from '@core/services/global-modal.service';
import { of, switchMap } from 'rxjs';
import { Cohort } from '@app/api/models/cohort.model';
import { UserNotificationService } from '@app/api/notifications/services/user-notification.service';
import { LearningActivityService } from '@app/api/learning/services/learning-activity.service';

@Component({
  selector: 'app-assign-learning-material-modal-button',
  templateUrl: './assign-learning-material-modal-button.component.html',
  styleUrls: ['./assign-learning-material-modal-button.component.sass'],
})
export class AssignLearningMaterialModalButtonComponent {
  @Input() content: LearningMaterialData | null = null;
  @ContentChild(TemplateRef) template?: TemplateRef<any>;
  @ViewChild('assignModal') assignModal?: TemplateRef<any>;

  @Input() selectedUsers: User[] = [];
  @Input() selectedCohorts: Cohort[] = [];

  assigning = false;

  context: any = {
    openModal: () => this.openModal(),
  };

  constructor(
    private modal: NgbModal,
    private globalModal: GlobalModalService,
    private userNotificationService: UserNotificationService,
    private modalService: GlobalModalService,
    private http: HttpClient,
    private api: ApiService,
    private learningActivityService: LearningActivityService,
  ) {}

  openModal() {
    this.modal.open(this.assignModal, { size: 'xl' });
  }

  closeModal() {
    this.selectedUsers = [];
    this.selectedCohorts = [];
    this.modal.dismissAll();
  }

  assign() {
    if (!this.content) return;

    this.assigning = true;

    const selectedUserIds = this.selectedUsers.map((u) => u.id);
    const selectedCohortIds = this.selectedCohorts
      .map((c) => c.id!)
      .filter((id) => id !== undefined);

    const request = {
      type: this.content!.materialType!,
      materialId: this.content!.id!,
      userIds: selectedUserIds,
      cohortIds: selectedCohortIds,
      dueDate: new Date(),
    };

    this.learningActivityService
      .checkIfMaterialIsAlreadyAssignedToStudent(request)
      .pipe(
        switchMap((value) => {
          if (value.isAssigned) {
            return this.updateMaterial(request);
          } else {
            return this.assignMaterial(request);
          }
        }),
      )
      .subscribe({
        next: (v) => {
          if (v.length === 0) {
            this.globalModal.alert({
              type: 'warning',
              title: 'No changes made',
              content: 'No changes were made to the assignment.',
            });
            this.assigning = false;
            return;
          }
          this.globalModal.icon(
            `Successfully assigned the ${this.content?.materialType?.toLowerCase()}`,
            'bi-check-circle-fill text-success',
            { okButtonCallback: () => this.closeModal() },
          );
          this.userNotificationService.updateNotifications();
          this.assigning = false;
        },
        error: (err) => {
          this.assigning = false;
          this.globalModal.alert({
            type: 'danger',
            title: 'Error',
            content:
              err.error?.message ||
              'An unexpected error occurred during assignment.',
          });
        },
      });
  }

  assignMaterial(request: CreateLearningActivityRequest) {
    return this.http.post<LearningActivity[]>(
      this.api.endpoint('/learning-activities'),
      request,
    );
  }

  updateMaterial(request: CreateLearningActivityRequest) {
    return this.globalModal
      .select(
        'The material is already assigned to the selected users. what would like to do?',
        [
          {
            label: 'Update Only: Apply new content to existing activities.',
            value: 'UPDATE',
          },
          {
            label:
              'Replace & Remove: Update activities with new content and remove previous content',
            value: 'UPDATE_AND_REMOVE',
          },
          {
            label: 'Keep Current: Do not change the current activity content.',
            value: 'none',
          },
        ],
        {
          okButtonText: 'Confirm',
          cancelButtonText: 'Cancel',
          type: 'warning',
          dismissable: false,
        },
      )
      .pipe(
        switchMap((value) => {
          if (value === 'none' || !value) {
            return of([]);
          }
          return this.http.post<LearningActivity[]>(
            this.api.endpoint('/learning-activities/update'),
            { ...request, action: value.toUpperCase() },
          );
        }),
      );
  }
}
